import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import TermsModal from '../components/TermsModal';
import axios from 'axios';
import './Home.css';
import '../styles/pageAnimation.css';

function Contact() {
  const [isMobile, setIsMobile] = useState(false);
  const [showGreenBar, setShowGreenBar] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-fill email and name for logged-in users
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setIsLoggedIn(true);
        setFormData(prev => ({
          ...prev,
          name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || prev.name,
          email: userData.email || prev.email
        }));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Hide green bar on scroll (no animation, instant show/hide)
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${process.env.REACT_APP_API}/api/contact/message`, formData);
      
      setFormStatus({ 
        type: 'success', 
        message: response.data.message || 'Your message has been sent successfully!' 
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: error.response?.data?.message || 'Failed to send message. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const scaleIn = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] }
  };

  return (
    <div style={{ background: '#f8fdf9', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>
      <div style={{ width: '100%', flex: 1 }} className="page-content-fade-in">
      
      {/* Main Content */}
      <div style={{ 
        width: '100%', 
        maxWidth: isMobile ? '100%' : '1200px', 
        margin: '0 auto', 
        padding: isMobile ? '40px 20px 80px' : '60px 40px 100px'
      }}>
        
        {/* Hero Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ 
            textAlign: 'center', 
            marginBottom: isMobile ? '48px' : '72px',
            paddingTop: isMobile ? '0' : '0'
          }}>
          
          {/* Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, delay: 0.3, type: "spring", stiffness: 100 }}
            style={{
              width: isMobile ? '64px' : '80px',
              height: isMobile ? '64px' : '80px',
              margin: '0 auto 24px',
              background: '#175c36',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(23, 92, 54, 0.25)',
              border: '3px solid rgba(255, 255, 255, 0.9)'
            }}>
            <svg width={isMobile ? "32" : "40"} height={isMobile ? "32" : "40"} viewBox="0 0 24 24" fill="white">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
          </motion.div>

          <motion.h1 
            {...fadeInUp}
            transition={{ delay: 0.4 }}
            style={{ 
              fontSize: isMobile ? '2.25rem' : '3.5rem', 
              fontWeight: 800, 
              color: '#15803d',
              marginBottom: '16px',
              letterSpacing: '-0.03em',
              lineHeight: 1.1
            }}>
            Get in Touch
          </motion.h1>
          
          <motion.p 
            {...fadeInUp}
            transition={{ delay: 0.5 }}
            style={{ 
              fontSize: isMobile ? '1rem' : '1.2rem', 
              color: '#166534', 
              maxWidth: '600px', 
              margin: '0 auto', 
              lineHeight: 1.6,
              fontWeight: 500,
              padding: isMobile ? '0 20px' : '0'
            }}>
            We're here to help bring your garden dreams to life 🌱
          </motion.p>
        </motion.div>

        {/* Contact Methods - Grid */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: isMobile ? '16px' : '24px', 
            marginBottom: isMobile ? '48px' : '80px' 
          }}>
          
          {/* Email Card */}
          <motion.a
            variants={fadeInUp}
            href="mailto:chiquisgardenlandscapingservic@gmail.com"
            whileHover={{ y: -8, boxShadow: '0 16px 32px rgba(23, 92, 54, 0.12)' }}
            whileTap={{ scale: 0.98 }}
            style={{ 
              padding: isMobile ? '28px 20px' : '40px 32px',
              background: 'white',
              borderRadius: '20px',
              border: '2px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              textDecoration: 'none',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden'
            }}>
            {/* Hover gradient */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: '#175c36',
              opacity: 0,
              transition: 'opacity 0.3s'
            }} className="card-accent" />
            
            <div style={{ 
              width: isMobile ? '48px' : '56px', 
              height: isMobile ? '48px' : '56px', 
              borderRadius: '16px', 
              background: '#f0fdf4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              boxShadow: '0 4px 12px rgba(23, 92, 54, 0.1)'
            }}>
              <svg width={isMobile ? "24" : "28"} height={isMobile ? "24" : "28"} viewBox="0 0 24 24" fill="#175c36">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
            </div>
            
            <h3 style={{ 
              fontSize: isMobile ? '1rem' : '1.15rem', 
              fontWeight: 700, 
              color: '#1e293b', 
              marginBottom: '8px',
              letterSpacing: '-0.01em'
            }}>
              Email Us
            </h3>
            
            <p style={{ 
              color: '#175c36', 
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              textAlign: 'center',
              wordBreak: 'break-word',
              lineHeight: 1.5,
              fontWeight: 500,
              margin: 0
            }}>
              chiquisgardenlandscapingservic@gmail.com
            </p>
          </motion.a>
          
          {/* Phone Card */}
          <motion.a
            variants={fadeInUp}
            href="tel:+639368983053"
            whileHover={{ y: -8, boxShadow: '0 16px 32px rgba(23, 92, 54, 0.12)' }}
            whileTap={{ scale: 0.98 }}
            style={{ 
              padding: isMobile ? '28px 20px' : '40px 32px',
              background: 'white',
              borderRadius: '20px',
              border: '2px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              textDecoration: 'none',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden'
            }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: '#175c36',
              opacity: 0,
              transition: 'opacity 0.3s'
            }} className="card-accent" />
            
            <div style={{ 
              width: isMobile ? '48px' : '56px', 
              height: isMobile ? '48px' : '56px', 
              borderRadius: '16px', 
              background: '#f0fdf4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              boxShadow: '0 4px 12px rgba(23, 92, 54, 0.1)'
            }}>
              <svg width={isMobile ? "24" : "28"} height={isMobile ? "24" : "28"} viewBox="0 0 24 24" fill="#175c36">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
            </div>
            
            <h3 style={{ 
              fontSize: isMobile ? '1rem' : '1.15rem', 
              fontWeight: 700, 
              color: '#1e293b', 
              marginBottom: '8px',
              letterSpacing: '-0.01em'
            }}>
              Call / Viber
            </h3>
            
            <p style={{ 
              color: '#175c36', 
              fontSize: isMobile ? '0.95rem' : '1rem',
              fontWeight: 600,
              margin: 0,
              letterSpacing: '0.5px'
            }}>
              +63 936 898 3053
            </p>
          </motion.a>
          
          {/* Location Card */}
          <motion.div
            variants={fadeInUp}
            whileHover={{ y: -8, boxShadow: '0 16px 32px rgba(23, 92, 54, 0.12)' }}
            style={{ 
              padding: isMobile ? '28px 20px' : '40px 32px',
              background: 'white',
              borderRadius: '20px',
              border: '2px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              position: 'relative',
              overflow: 'hidden'
            }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: '#175c36',
              opacity: 0,
              transition: 'opacity 0.3s'
            }} className="card-accent" />
            
            <div style={{ 
              width: isMobile ? '48px' : '56px', 
              height: isMobile ? '48px' : '56px', 
              borderRadius: '16px', 
              background: '#f0fdf4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              boxShadow: '0 4px 12px rgba(23, 92, 54, 0.1)'
            }}>
              <svg width={isMobile ? "24" : "28"} height={isMobile ? "24" : "28"} viewBox="0 0 24 24" fill="#175c36">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            
            <h3 style={{ 
              fontSize: isMobile ? '1rem' : '1.15rem', 
              fontWeight: 700, 
              color: '#1e293b', 
              marginBottom: '8px',
              letterSpacing: '-0.01em'
            }}>
              Visit Us
            </h3>
            
            <p style={{ 
              color: '#475569', 
              fontSize: isMobile ? '0.9rem' : '0.95rem', 
              margin: 0, 
              textAlign: 'center', 
              fontWeight: 500 
            }}>
              Pulong Iba, Silang, Cavite
            </p>
          </motion.div>
        </motion.div>

        {/* Customer Message Form */}
        <motion.div
          {...scaleIn}
          transition={{ delay: 0.45 }}
          style={{ 
            background: 'white',
            borderRadius: '24px',
            padding: isMobile ? '32px 24px' : '48px',
            marginBottom: isMobile ? '48px' : '56px',
            border: '2px solid #e2e8f0',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
          }}>
          
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '36px' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.55, type: "spring" }}
              style={{
                width: isMobile ? '56px' : '64px',
                height: isMobile ? '56px' : '64px',
                margin: '0 auto 20px',
                background: '#175c36',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(23, 92, 54, 0.2)'
              }}>
              <svg width={isMobile ? "28" : "32"} height={isMobile ? "28" : "32"} viewBox="0 0 24 24" fill="white">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
              </svg>
            </motion.div>
            
            <h2 style={{ 
              fontSize: isMobile ? '1.75rem' : '2rem', 
              fontWeight: 700,
              color: '#1e293b',
              marginBottom: '12px',
              letterSpacing: '-0.02em'
            }}>
              Send Us a Message
            </h2>
            
            <p style={{ 
              fontSize: isMobile ? '0.95rem' : '1rem', 
              color: '#64748b',
              margin: 0
            }}>
              Have questions or concerns? We're here to help!
            </p>
          </div>

          {formStatus.message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                padding: '16px 20px',
                borderRadius: '12px',
                marginBottom: '24px',
                background: formStatus.type === 'success' 
                  ? '#f0fdf4'
                  : '#fef2f2',
                border: formStatus.type === 'success'
                  ? '2px solid #bbf7d0'
                  : '2px solid #fecaca',
                color: formStatus.type === 'success' ? '#15803d' : '#dc2626',
                fontWeight: 500,
                fontSize: '0.95rem',
                textAlign: 'center'
              }}>
              {formStatus.message}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: '#1e293b',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}>
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isLoggedIn}
                  placeholder={isLoggedIn ? "Using your registered name" : "Enter your full name"}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: isLoggedIn ? '#f8f9fa' : 'white',
                    cursor: isLoggedIn ? 'not-allowed' : 'text',
                    color: isLoggedIn ? '#64748b' : 'inherit'
                  }}
                  onFocus={(e) => !isLoggedIn && (e.target.style.borderColor = '#175c36')}
                  onBlur={(e) => !isLoggedIn && (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: '#1e293b',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isLoggedIn}
                  placeholder={isLoggedIn ? "Using your registered email" : "your.email@example.com"}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: isLoggedIn ? '#f8f9fa' : 'white',
                    cursor: isLoggedIn ? 'not-allowed' : 'text',
                    color: isLoggedIn ? '#64748b' : 'inherit'
                  }}
                  onFocus={(e) => !isLoggedIn && (e.target.style.borderColor = '#175c36')}
                  onBlur={(e) => !isLoggedIn && (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#1e293b',
                fontWeight: 600,
                fontSize: '0.95rem'
              }}>
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                placeholder="What is this about?"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#175c36'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#1e293b',
                fontWeight: 600,
                fontSize: '0.95rem'
              }}>
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder="Tell us more about your inquiry or concern..."
                rows="6"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#175c36'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              style={{
                padding: isMobile ? '16px 32px' : '18px 48px',
                background: isSubmitting 
                  ? '#9ca3af'
                  : '#175c36',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.05rem',
                fontWeight: 600,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(23, 92, 54, 0.25)',
                letterSpacing: '0.5px',
                alignSelf: 'center'
              }}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </motion.button>
          </form>
        </motion.div>
          
        {/* Social Media Section */}
        <motion.div
          {...scaleIn}
          transition={{ delay: 0.5 }}
          style={{ 
            background: 'white',
            borderRadius: '24px',
            padding: isMobile ? '32px 24px' : '56px 48px',
            marginBottom: isMobile ? '48px' : '80px',
            border: '2px solid #e2e8f0',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
          }}>
          
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '40px' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6, type: "spring" }}
              style={{
                width: isMobile ? '56px' : '64px',
                height: isMobile ? '56px' : '64px',
                margin: '0 auto 20px',
                background: '#175c36',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(23, 92, 54, 0.2)'
              }}>
              <svg width={isMobile ? "28" : "32"} height={isMobile ? "28" : "32"} viewBox="0 0 24 24" fill="white">
                <path d="M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
              </svg>
            </motion.div>
            
            <h2 style={{ 
              fontSize: isMobile ? '1.75rem' : '2rem', 
              fontWeight: 700,
              color: '#1e293b',
              marginBottom: '12px',
              letterSpacing: '-0.02em'
            }}>
              Connect With Us
            </h2>
            
            <p style={{ 
              fontSize: isMobile ? '0.95rem' : '1rem', 
              color: '#64748b',
              margin: 0
            }}>
              Choose your preferred platform to get in touch
            </p>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))', 
            gap: isMobile ? '16px' : '20px', 
            maxWidth: '900px', 
            margin: '0 auto' 
          }}>
            
            {/* Messenger */}
            <motion.a
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.95 }}
              href="https://www.messenger.com/t/61552636677232" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Contact us on Messenger"
              style={{ 
                padding: isMobile ? '24px' : '32px 24px', 
                background: '#f0f9ff', 
                borderRadius: '20px', 
                border: '2px solid #bae6fd',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(0, 132, 255, 0.08)',
                cursor: 'pointer'
              }}>
              <svg width={isMobile ? "36" : "40"} height={isMobile ? "36" : "40"} viewBox="0 0 24 24" fill="none" style={{ marginBottom: '16px' }}>
                <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.897 1.445 5.482 3.705 7.168V22l3.418-1.877c.912.252 1.875.387 2.877.387 5.523 0 10-4.145 10-9.267C22 6.145 17.523 2 12 2zm.993 12.492l-2.549-2.718-4.975 2.718 5.467-5.803 2.61 2.718 4.915-2.718-5.468 5.803z" fill="#0084ff"/>
              </svg>
              <h3 style={{ 
                fontSize: isMobile ? '1rem' : '1.05rem', 
                fontWeight: 600, 
                color: '#0369a1', 
                margin: 0 
              }}>
                Messenger
              </h3>
            </motion.a>

            {/* WhatsApp */}
            <motion.a
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.95 }}
              href="https://api.whatsapp.com/send?phone=%2B639368983053&context=AfdZJ4Gw4qXS2hwvkTvAM5U9sYCtbI1FOkbV1JuyvbJLkBBN5KENo_9CWRwo11oC5A9jfB47D1WGRIppN0xlSH1eSmNduvS20BjzGsTTKzYEBACiF70Vg1EAzInCYst_hFwiADQwLu6dOxjEhrVli6WpsA&source=FB_Page&app=facebook&entry_point=page_cta" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Contact us on WhatsApp"
              style={{ 
                padding: isMobile ? '24px' : '32px 24px', 
                background: '#f0fdf4', 
                borderRadius: '20px', 
                border: '2px solid #bbf7d0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.08)',
                cursor: 'pointer'
              }}>
              <svg width={isMobile ? "36" : "40"} height={isMobile ? "36" : "40"} viewBox="0 0 24 24" fill="none" style={{ marginBottom: '16px' }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="#25d366"/>
              </svg>
              <h3 style={{ 
                fontSize: isMobile ? '1rem' : '1.05rem', 
                fontWeight: 600, 
                color: '#15803d', 
                margin: 0 
              }}>
                WhatsApp
              </h3>
            </motion.a>

            {/* Facebook */}
            <motion.a
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.95 }}
              href="https://www.facebook.com/profile.php?id=61552636677232" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Visit our Facebook page"
              style={{ 
                padding: isMobile ? '24px' : '32px 24px', 
                background: '#eff6ff', 
                borderRadius: '20px', 
                border: '2px solid #bfdbfe',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(24, 119, 242, 0.08)',
                cursor: 'pointer'
              }}>
              <svg width={isMobile ? "36" : "40"} height={isMobile ? "36" : "40"} viewBox="0 0 24 24" fill="none" style={{ marginBottom: '16px' }}>
                <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971h-1.513c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" fill="#1877f2"/>
              </svg>
              <h3 style={{ 
                fontSize: isMobile ? '1rem' : '1.05rem', 
                fontWeight: 600, 
                color: '#1e40af', 
                margin: 0 
              }}>
                Facebook
              </h3>
            </motion.a>
          </div>
        </motion.div>
          
        {/* Business Hours */}
        <motion.div
          {...scaleIn}
          transition={{ delay: 0.6 }}
          style={{ 
            background: 'white',
            borderRadius: '24px',
            padding: isMobile ? '32px 24px' : '48px',
            border: '2px solid #e2e8f0',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            maxWidth: '800px',
            margin: '0 auto',
            marginBottom: isMobile ? '48px' : '0'
          }}>
          
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '36px' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.7, type: "spring" }}
              style={{
                width: isMobile ? '56px' : '64px',
                height: isMobile ? '56px' : '64px',
                margin: '0 auto 20px',
                background: '#175c36',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(23, 92, 54, 0.2)'
              }}>
              <svg width={isMobile ? "28" : "32"} height={isMobile ? "28" : "32"} viewBox="0 0 24 24" fill="white">
                <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
              </svg>
            </motion.div>
            
            <h3 style={{ 
              fontSize: isMobile ? '1.5rem' : '1.75rem', 
              fontWeight: 700, 
              color: '#1e293b', 
              marginBottom: '8px',
              letterSpacing: '-0.02em'
            }}>
              Business Hours
            </h3>
            
            <p style={{ 
              fontSize: isMobile ? '0.9rem' : '0.95rem', 
              color: '#64748b',
              margin: 0
            }}>
              We're here to serve you
            </p>
          </div>
          
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '16px' }}>
            
            <motion.div 
              variants={fadeInUp}
              whileHover={{ x: 4 }}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: isMobile ? '16px 20px' : '20px 28px',
                background: '#f0fdf4',
                borderRadius: '16px',
                border: '2px solid #bbf7d0',
                transition: 'all 0.3s ease'
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  background: '#22c55e' 
                }} />
                <span style={{ 
                  fontSize: isMobile ? '1rem' : '1.1rem', 
                  fontWeight: 600, 
                  color: '#15803d' 
                }}>
                  Monday to Sunday
                </span>
              </div>
              <span style={{ 
                fontSize: isMobile ? '0.95rem' : '1.05rem', 
                color: '#175c36', 
                fontWeight: 600 
              }}>
                7:00 am - 8:00 pm
              </span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Footer */}
      <footer style={{
        width: '100%',
        background: '#0f4527',
        marginTop: 'auto',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px 20px',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Main Footer Content */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            alignItems: 'start',
            marginBottom: '35px'
          }}>
            {/* Connect Section - Hidden on mobile */}
            {!isMobile && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Connect With Us</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.facebook.com/profile.php?id=61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.messenger.com/t/61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Messenger"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.875 1.44 5.449 3.687 7.126V22l3.486-1.914c.93.257 1.913.395 2.827.395 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.993 12.437l-2.552-2.725-4.983 2.725 5.48-5.813 2.613 2.725 4.922-2.725-5.48 5.813z"/>
                  </svg>
                </a>
                <a
                  href="viber://chat?number=639368983053"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.644s-.037.977.61 1.177c.777.242 1.234-.5 1.977-1.303.41-.44.972-1.084 1.397-1.58 3.85.326 6.812-.417 7.15-.526.776-.253 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.026-.017z"/>
                  </svg>
                </a>
              </div>
            </div>}

            {/* Logo Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              justifySelf: 'center',
              marginBottom: isMobile ? '10px' : '0'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2.5px solid rgba(255, 255, 255, 0.25)',
                padding: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                <img
                  src="/images/chiqui_logo_white-outline.svg"
                  alt="Chiqui's Garden Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>Chiqui's Garden</div>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 400
                }}>© {new Date().getFullYear()} All Rights Reserved</div>
              </div>
            </div>

            {/* Location Section - Hidden on mobile */}
            {!isMobile && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifySelf: 'end'
            }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Our Location</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                padding: '14px 18px',
                borderRadius: '16px',
                border: '1.5px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.95)">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: '3px',
                    letterSpacing: '0.3px'
                  }}>Iba Garden</div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Pulong Iba, Silang, Cavite</div>
                </div>
              </div>
            </div>}
          </div>

          {/* Divider */}
          <div style={{
            width: '100%',
            height: '1px',
            background: 'rgba(255,255,255,0.2)',
            margin: isMobile ? '20px 0' : '25px 0'
          }} />

          {/* Bottom Links */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '8px' : '30px',
            paddingBottom: '10px'
          }}>
            <a
              href="/about"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              About Us
            </a>
            <a
              href="/contact"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Contact Info
            </a>
            <a
              href="/return-policy"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Return & Exchange Policy
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Terms of Use
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
      </div>
      
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isPrivacyPolicy={true}
      />
    </div>
  );
}

export default Contact;
